import type { Vec2 } from '../math/vec';
import { v } from '../math/vec';
import type { Track, SurfaceRegion, Ramp, PropPlacement } from '../sim/track';

// The v1 track: a kitchen table, 64 x 40 metres, top-left at (0,0), world y
// grows downward. A sports car is about 1.0 m long.
//
// PropPlacement.scale is metres per 100 px of the sprite at its native
// resolution (max dimension 768 px). A 768 px wide sprite at scale 0.5 is
// 3.84 m wide. The renderer must use this same convention.

// ---------------------------------------------------------------------
// Sprite ids that exist as generated PNGs (packages/client/public/assets/
// props/*.png), shared with tests and the renderer for validating prop
// placements and preloading assets.
// ---------------------------------------------------------------------
export const KITCHEN_TABLE_PROP_IDS = [
  'cereal_box',
  'mug',
  'plate',
  'spoon',
  'fork',
  'knife',
  'toast',
  'butter_dish',
  'salt',
  'pepper',
  'jam_jar',
  'sugar_cubes',
  'napkin',
  'banana',
  'egg_cup',
  'juice_carton',
  'milk_spill',
  'crumbs',
] as const;

// ---------------------------------------------------------------------
// Centreline: a closed Catmull-Rom loop through hand-placed waypoints,
// resampled to even-ish arc length. Building it from waypoints (rather
// than hand-typing 50 raw points) keeps the shape reviewable and lets the
// loop be re-tuned by moving a handful of named points.
// ---------------------------------------------------------------------

function catmullRom(p0: Vec2, p1: Vec2, p2: Vec2, p3: Vec2, t: number): Vec2 {
  const t2 = t * t;
  const t3 = t2 * t;
  const x =
    0.5 *
    (2 * p1.x +
      (-p0.x + p2.x) * t +
      (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
      (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);
  const y =
    0.5 *
    (2 * p1.y +
      (-p0.y + p2.y) * t +
      (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
      (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3);
  return { x, y };
}

// Dense closed Catmull-Rom sample through waypoints, samplesPerSeg per span.
function sampleClosedSpline(waypoints: Vec2[], samplesPerSeg: number): Vec2[] {
  const n = waypoints.length;
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const p0 = waypoints[(i - 1 + n) % n]!;
    const p1 = waypoints[i]!;
    const p2 = waypoints[(i + 1) % n]!;
    const p3 = waypoints[(i + 2) % n]!;
    for (let s = 0; s < samplesPerSeg; s++) {
      out.push(catmullRom(p0, p1, p2, p3, s / samplesPerSeg));
    }
  }
  return out;
}

function pointAtArcLength(dense: Vec2[], cum: number[], target: number): Vec2 {
  const n = dense.length;
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cum[mid]! < target) lo = mid + 1;
    else hi = mid;
  }
  const i = Math.max(1, lo);
  const segStart = cum[i - 1]!;
  const segEnd = cum[i]!;
  const segLen = segEnd - segStart;
  const localT = segLen === 0 ? 0 : (target - segStart) / segLen;
  const a = dense[(i - 1) % n]!;
  const b = dense[i % n]!;
  return { x: a.x + (b.x - a.x) * localT, y: a.y + (b.y - a.y) * localT };
}

// Resamples a dense closed polyline to approximately targetCount points,
// aiming for even arc-length spacing but nudging forward whenever the
// local curvature is tight enough that an arc-length step would produce a
// too-short Euclidean chord (arc length and chord length diverge on tight
// turns, e.g. the hairpin). Guarantees every consecutive pair is at least
// minChord apart in straight-line distance, which is what actually needs
// to hold for the track to be drivable.
function resampleEven(dense: Vec2[], targetCount: number, minChord: number): Vec2[] {
  const n = dense.length;
  const cum: number[] = [0];
  for (let i = 1; i <= n; i++) {
    const a = dense[i - 1]!;
    const b = dense[i % n]!;
    cum.push(cum[i - 1]! + v.dist(a, b));
  }
  const total = cum[n]!;
  const step = total / targetCount;

  const out: Vec2[] = [{ ...dense[0]! }];
  let target = step;
  while (out.length < targetCount) {
    let point = pointAtArcLength(dense, cum, target % total);
    const prev = out[out.length - 1]!;
    let guard = 0;
    while (v.dist(point, prev) < minChord && guard < 500) {
      target += step * 0.05;
      point = pointAtArcLength(dense, cum, target % total);
      guard++;
    }
    out.push(point);
    target += step;
  }
  return out;
}

// Hand-placed waypoints tracing the loop described in the design brief,
// left to right: start/finish on the bottom edge, a sweep up the right
// side around the mug into a hairpin around the salt shaker, a straight
// along the top past the cereal box and toast ramp, an S-bend down the
// left side between the jam jar and butter dish through the milk spill
// and crumbs patch, and a chicane between the fork and knife back to
// start. The loop uses nearly the full 64 x 40 m table.
//
// This shape replaced an earlier version (and an intermediate version
// with a single sharp S-bend apex) that only cleared width + 1.5 m
// between non-adjacent stretches -- close enough to read as "touching"
// wood. The S-bend here is spread across two gentler bends (waypoints 11
// and 13 below) rather than one sharp switchback, which is what lets it
// reach the ascent's amplitude (apex near x=41) without the two legs of
// the bend running close to each other. Every pair of centreline points
// more than 12 m apart along the loop now sits at least width + 4 m
// (9 m) apart in space -- see the "keeps every non-adjacent centreline
// stretch at least one track width apart" test in kitchen-table.test.ts,
// which enforces this directly so the shape cannot regress silently.
const WAYPOINTS: Vec2[] = [
  { x: 19, y: 36.8 }, // start/finish, bottom straight, beside the plate
  { x: 16.52, y: 36.711 }, // bottom straight, gentle bow toward the table edge
  { x: 33.838, y: 36.782 }, // bottom straight
  { x: 53.324, y: 33.812 }, // approach right side
  { x: 60.5, y: 34.414 }, // sweep up around the mug
  { x: 60.5, y: 11.034 }, // continue up the right edge, close to the table edge
  { x: 58.303, y: 3.2 }, // approach the hairpin, near the top-right corner
  { x: 44, y: 3.594 }, // hairpin tip near the salt shaker
  { x: 41, y: 5.771 }, // top straight, past the cereal box and toast ramp
  { x: 10.97, y: 3.303 }, // top straight continues, near the top-left corner
  { x: 3.457, y: 11.456 }, // curve down, jam jar apex (S-bend start)
  { x: 17.289, y: 14.165 }, // S-bend, first gentle bend on the way out
  { x: 41.059, y: 16.483 }, // S-bend apex, swung wide into the open middle of the table
  { x: 31.542, y: 26.817 }, // S-bend, second gentle bend on the way back in
  { x: 4.289, y: 26 }, // S-bend second apex, butter dish
  { x: 4.639, y: 35.043 }, // bottom-left chicane, fork side
  { x: 3.409, y: 36.8 }, // bottom-left chicane, knife side
];

const SAMPLES_PER_SEGMENT = 200;
const TARGET_POINTS = 50;
const MIN_CHORD = 1.2;

const CENTRELINE: Vec2[] = resampleEven(
  sampleClosedSpline(WAYPOINTS, SAMPLES_PER_SEGMENT),
  TARGET_POINTS,
  MIN_CHORD,
);

// Cumulative arc length around the finished centreline and its total
// length, used to place the start grid by true distance-behind-the-line
// (see pointAtSignedArcLength below) rather than by linearly extrapolating
// from the start tangent: the track curves through the bottom-left chicane
// just behind the start/finish line, so a straight extrapolation puts the
// rear grid rows well off the racing line and heading.
const CL_N = CENTRELINE.length;
const CL_CUM: number[] = [0];
for (let i = 1; i <= CL_N; i++) {
  const a = CENTRELINE[i - 1]!;
  const b = CENTRELINE[i % CL_N]!;
  CL_CUM.push(CL_CUM[i - 1]! + v.dist(a, b));
}
const CL_LENGTH = CL_CUM[CL_N]!;

// Position and direction of travel at signed arc length s along the loop
// (s may be negative, e.g. "3.5 m behind the start line"); wraps modulo
// the loop length. Mirrors sim/track.ts's pointAtProgress, duplicated here
// (rather than imported) because it only needs the centreline, which is
// all that exists at this point while the track is being built.
function pointAtSignedArcLength(s: number): { pos: Vec2; tangent: number } {
  const sMod = ((s % CL_LENGTH) + CL_LENGTH) % CL_LENGTH;
  let segIndex = CL_N - 1;
  for (let i = 0; i < CL_N; i++) {
    const start = CL_CUM[i]!;
    const end = i + 1 < CL_N ? CL_CUM[i + 1]! : CL_LENGTH;
    if (sMod >= start && sMod < end) {
      segIndex = i;
      break;
    }
  }
  const a = CENTRELINE[segIndex]!;
  const b = CENTRELINE[(segIndex + 1) % CL_N]!;
  const segLen = v.dist(a, b);
  const localT = segLen === 0 ? 0 : (sMod - CL_CUM[segIndex]!) / segLen;
  const pos = v.lerp(a, b, localT);
  const dir = v.norm(v.sub(b, a));
  return { pos, tangent: Math.atan2(dir.y, dir.x) };
}

// ---------------------------------------------------------------------
// Geometry helpers used to place walls, surfaces, the ramp and props
// relative to the generated centreline, so they stay attached to the
// track shape if the waypoints above are tuned later.
// ---------------------------------------------------------------------

function tangentAt(i: number): Vec2 {
  const n = CENTRELINE.length;
  const prev = CENTRELINE[(i - 1 + n) % n]!;
  const next = CENTRELINE[(i + 1) % n]!;
  return v.norm(v.sub(next, prev));
}

// Perpendicular to the direction of travel, positive toward the right of
// travel (matches the "right" convention used by progressAlong in track.ts).
function rightAt(i: number): Vec2 {
  const d = tangentAt(i);
  return { x: -d.y, y: d.x };
}

// A point offset from centreline[i]: lateral is perpendicular to travel
// (positive = right of travel), along is parallel to travel.
function offsetPoint(i: number, lateral: number, along = 0): Vec2 {
  const base = CENTRELINE[i]!;
  const dir = tangentAt(i);
  const right = rightAt(i);
  return v.add(v.add(base, v.scale(right, lateral)), v.scale(dir, along));
}

function regularPolygon(center: Vec2, radius: number, sides: number, rotation = 0): Vec2[] {
  const pts: Vec2[] = [];
  for (let k = 0; k < sides; k++) {
    const a = rotation + (k / sides) * Math.PI * 2;
    pts.push({ x: center.x + radius * Math.cos(a), y: center.y + radius * Math.sin(a) });
  }
  return pts;
}

// A rectangle centred on `center`, halfLength along direction `angle`
// (radians) and halfWidth perpendicular to it. Vertices are wound
// consistently (no self-intersection) so it is a valid convex wall.
function rotatedRect(center: Vec2, halfLength: number, halfWidth: number, angle: number): Vec2[] {
  const dir = { x: Math.cos(angle), y: Math.sin(angle) };
  const right = { x: -dir.y, y: dir.x };
  const alongVec = v.scale(dir, halfLength);
  const acrossVec = v.scale(right, halfWidth);
  return [
    v.add(v.add(center, alongVec), acrossVec),
    v.add(v.sub(center, alongVec), acrossVec),
    v.sub(v.sub(center, alongVec), acrossVec),
    v.sub(v.add(center, alongVec), acrossVec),
  ];
}

function angleAt(i: number): number {
  const d = tangentAt(i);
  return Math.atan2(d.y, d.x);
}

// ---------------------------------------------------------------------
// Walls: convex polygons approximating prop footprints at ~90% of their
// visual size, so cars can brush the sprites without clipping through.
// ---------------------------------------------------------------------

const MUG_POS = offsetPoint(9, -5.5, 0);
// The mug prop is placed with scale 0.52 (see PROPS below); wall radius must
// use that same scale, not an assumed 1.0, or the wall shrinks to a fraction
// of the visible sprite.
const MUG_SCALE = 0.52;
const MUG_WALL_RADIUS = (0.9 * (552 * MUG_SCALE)) / 100 / 2; // mug sprite height 552px * placement scale, /2 for radius

const SALT_POS = offsetPoint(17, -4, 0);
const SALT_WALL_RADIUS = 0.9 * (768 * 0.29) / 100 / 2;

const CEREAL_POS = offsetPoint(18, -12, 0);
const CEREAL_ANGLE = angleAt(18);
const CEREAL_HALF_LENGTH = (0.9 * (768 * 1.302)) / 100 / 2; // ~10 m long, along the straight
const CEREAL_HALF_WIDTH = (0.9 * (438 * 1.302)) / 100 / 2; // ~6 m wide

const JAM_JAR_POS = offsetPoint(28, -6, 0);
const JAM_JAR_WALL_RADIUS = 0.9 * (768 * 0.34) / 100 / 2;

const BUTTER_DISH_POS = offsetPoint(44, 4.6, 0);
const BUTTER_DISH_ANGLE = angleAt(44) + Math.PI / 2;
const BUTTER_HALF_LENGTH = (0.9 * (768 * 0.52)) / 100 / 2; // ~4 m
const BUTTER_HALF_WIDTH = (0.9 * (466 * 0.52)) / 100 / 2; // ~2.4 m

const FORK_POS = offsetPoint(46, -2.3, -1);
const FORK_ANGLE = angleAt(46) + 0.5;
const FORK_HALF_LENGTH = (0.9 * (768 * 1.0417)) / 100 / 2; // ~8 m long
const FORK_HALF_WIDTH = (0.9 * (175 * 1.0417)) / 100 / 2;

const KNIFE_POS = offsetPoint(47, -2.8, 0.5);
const KNIFE_ANGLE = angleAt(47) - 0.5;
const KNIFE_HALF_LENGTH = (0.9 * (768 * 1.0417)) / 100 / 2; // ~8 m long
const KNIFE_HALF_WIDTH = (0.9 * (101 * 1.0417)) / 100 / 2;

const WALLS: Vec2[][] = [
  regularPolygon(MUG_POS, MUG_WALL_RADIUS, 12),
  regularPolygon(SALT_POS, SALT_WALL_RADIUS, 12),
  rotatedRect(CEREAL_POS, CEREAL_HALF_LENGTH, CEREAL_HALF_WIDTH, CEREAL_ANGLE),
  regularPolygon(JAM_JAR_POS, JAM_JAR_WALL_RADIUS, 12),
  rotatedRect(BUTTER_DISH_POS, BUTTER_HALF_LENGTH, BUTTER_HALF_WIDTH, BUTTER_DISH_ANGLE),
  rotatedRect(FORK_POS, FORK_HALF_LENGTH, FORK_HALF_WIDTH, FORK_ANGLE),
  rotatedRect(KNIFE_POS, KNIFE_HALF_LENGTH, KNIFE_HALF_WIDTH, KNIFE_ANGLE),
];

// ---------------------------------------------------------------------
// Surfaces: the milk spill and crumbs patch along the left side, after
// the S-bend and before the bottom-left chicane.
// ---------------------------------------------------------------------

const MILK_SPILL_POLY: Vec2[] = [
  offsetPoint(39, -2.4, -1),
  offsetPoint(39, 2.4, -1),
  offsetPoint(41, 2.4, 1),
  offsetPoint(41, -2.4, 1),
];

const CRUMBS_POLY: Vec2[] = [
  offsetPoint(41, -2.4, -0.5),
  offsetPoint(41, 2.4, -0.5),
  offsetPoint(43, 2.4, 0.5),
  offsetPoint(43, -2.4, 0.5),
];

const SURFACES: SurfaceRegion[] = [
  { poly: MILK_SPILL_POLY, name: 'milk_spill', mod: { speed: 1.05, grip: 0.55 } },
  { poly: CRUMBS_POLY, name: 'crumbs', mod: { speed: 0.7, grip: 1.1 } },
];

// ---------------------------------------------------------------------
// Ramp: the toast, laid across the top straight.
// ---------------------------------------------------------------------

const RAMP_INDEX = 24;
const RAMP_RECT: Vec2[] = rotatedRect(CENTRELINE[RAMP_INDEX]!, 1.5, 2.6, angleAt(RAMP_INDEX));
const RAMP: Ramp = {
  rect: RAMP_RECT,
  dir: angleAt(RAMP_INDEX),
  airTime: 0.55,
  minSpeed: 9,
};

// ---------------------------------------------------------------------
// Falloff: the four polygons outside the table rectangle. The right-side
// ascent now hugs the true table edge closely on its own (the legal
// band's right edge comes within ~0.4 m of x = 64 at its closest, see the
// band-clearance test below), so a separate notch carved into the table
// is no longer needed to create that edge-hugging hazard -- the real
// edge does it directly.
// ---------------------------------------------------------------------

const FALLOFF: Vec2[][] = [
  [
    { x: -60, y: -60 },
    { x: 0, y: -60 },
    { x: 0, y: 100 },
    { x: -60, y: 100 },
  ],
  [
    { x: 64, y: -60 },
    { x: 124, y: -60 },
    { x: 124, y: 100 },
    { x: 64, y: 100 },
  ],
  [
    { x: -60, y: -60 },
    { x: 124, y: -60 },
    { x: 124, y: 0 },
    { x: -60, y: 0 },
  ],
  [
    { x: -60, y: 40 },
    { x: 124, y: 40 },
    { x: 124, y: 100 },
    { x: -60, y: 100 },
  ],
];

// ---------------------------------------------------------------------
// Start grid: 6 slots in 2 columns, 1.4 m apart, behind the start line.
// Each row is placed by true arc length behind the line and takes the
// heading of the local tangent there (not a single shared heading), since
// the chicane just behind the start curves enough that a fixed heading
// would misalign the rear rows.
// ---------------------------------------------------------------------

const START_LEAD_IN = 3.5; // arc length behind the line for the front row
const START_ROW_SPACING = 1.4;
const START_COL_OFFSET = 0.7;
const startGrid: { pos: Vec2; heading: number }[] = [];
for (let row = 0; row < 3; row++) {
  const { pos, tangent } = pointAtSignedArcLength(-(START_LEAD_IN + row * START_ROW_SPACING));
  const right = { x: -Math.sin(tangent), y: Math.cos(tangent) };
  for (const colOffset of [-START_COL_OFFSET, START_COL_OFFSET]) {
    startGrid.push({
      pos: v.add(pos, v.scale(right, colOffset)),
      heading: tangent,
    });
  }
}

// ---------------------------------------------------------------------
// Props: 15 placements. Walls exist for cereal_box, mug, salt, jam_jar,
// butter_dish, fork and knife; the rest (plate, toast, spoon, sugar_cubes,
// napkin, banana, egg_cup, pepper) are purely visual.
// ---------------------------------------------------------------------

const PROPS: PropPlacement[] = [
  { sprite: 'plate', pos: offsetPoint(0, -3, 1.5), rot: 0, scale: 1.17, layer: 'under' },
  { sprite: 'mug', pos: MUG_POS, rot: 0.3, scale: MUG_SCALE, layer: 'over' },
  { sprite: 'salt', pos: SALT_POS, rot: 0, scale: 0.29, layer: 'over' },
  { sprite: 'pepper', pos: offsetPoint(17, -6.5, -1), rot: 0.2, scale: 0.29, layer: 'under' },
  {
    sprite: 'cereal_box',
    pos: CEREAL_POS,
    rot: CEREAL_ANGLE,
    scale: 1.302,
    layer: 'over',
  },
  {
    sprite: 'toast',
    pos: CENTRELINE[RAMP_INDEX]!,
    rot: angleAt(RAMP_INDEX) + Math.PI / 2,
    scale: 0.651,
    layer: 'under',
  },
  { sprite: 'jam_jar', pos: JAM_JAR_POS, rot: 0, scale: 0.34, layer: 'over' },
  {
    sprite: 'butter_dish',
    pos: BUTTER_DISH_POS,
    rot: BUTTER_DISH_ANGLE,
    scale: 0.52,
    layer: 'over',
  },
  { sprite: 'fork', pos: FORK_POS, rot: FORK_ANGLE, scale: 1.0417, layer: 'over' },
  { sprite: 'knife', pos: KNIFE_POS, rot: KNIFE_ANGLE, scale: 1.0417, layer: 'over' },
  // spoon is purely decorative, dropped in open table space clear of the
  // band rather than offset from the tight chicane corner.
  { sprite: 'spoon', pos: { x: 15, y: 20 }, rot: 0.4, scale: 1.0417, layer: 'under' },
  { sprite: 'sugar_cubes', pos: offsetPoint(22, -7, -1), rot: 0, scale: 0.39, layer: 'under' },
  { sprite: 'napkin', pos: offsetPoint(1, -3, 0), rot: 0.1, scale: 0.65, layer: 'under' },
  // banana and egg_cup are purely decorative, dropped in open table space
  // clear of the (now much larger) legal band -- see the S-bend apex
  // around centreline[36] and the wide sweep of walls above.
  { sprite: 'banana', pos: { x: 50, y: 22 }, rot: 1.2, scale: 0.65, layer: 'under' },
  { sprite: 'egg_cup', pos: { x: 30, y: 20 }, rot: 0, scale: 0.326, layer: 'under' },
];

// ---------------------------------------------------------------------

export const KITCHEN_TABLE: Track = {
  id: 'kitchen-table',
  name: 'Kitchen Table',
  width: 5,
  centreline: CENTRELINE,
  walls: WALLS,
  surfaces: SURFACES,
  ramps: [RAMP],
  falloff: FALLOFF,
  startGrid,
  props: PROPS,
  bounds: { min: { x: 0, y: 0 }, max: { x: 64, y: 40 } },
  vehicle: 'sportsCar',
};

export const TRACKS: Record<string, Track> = {
  [KITCHEN_TABLE.id]: KITCHEN_TABLE,
};
